import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from '@mui/material/Modal'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { handleOpenInNewTab } from '../../services/articleCardHandlers'
import { useAuth } from '../../context/AuthContext'
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  Fab,
  Zoom,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material'
import { parseHtmlSanitizeAddTargetToLinks } from '../../services/utils'
import DecisionArticle from './DecisionArticle'
import useFindScopeLayer from '../../hooks/useFindScopeLayer'

// Color palette options - wide selection of options for you to choose from
const decisionTypeColors = {
  // Primary Colors
  'CD - Cabinet Decision': {
    main: '#4e342e', // Brown
    light: '#efebe9',
  },
  'MD - Ministerial Decision': {
    main: '#1976d2', // Blue
    light: '#e3f2fd',
  },
  'FTA - Federal Tax Authority Decision': {
    main: '#2e7d32', // Green
    light: '#e8f5e9',
  },
  'AD - Administrative Decision': {
    main: '#4e342e', // Brown
    light: '#efebe9',
  },
  'ZD - ZATCA Decision': {
    main: '#d81b60', // Pink
    light: '#fce4ec',
  },
  'BL - Bylaws': {
    main: '#0097a7', // Teal
    light: '#e0f7fa',
  },
  'ER - Executive Rules and Instructions': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'ERS - Executive Regulations': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'IR - Implementing Regulations': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'EB - Executive Bylaws': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'JD - Judicial Decision': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'DECISION - Public Clarification': {
    main: '#7b1fa2', // Purple
    light: '#f3e5f5',
  },
  'DECISION - FTA Admin Penalty': {
    main: '#ff9800', // Orange
    light: '#fff3e0',
  },
  'DECISION - UAE Supreme Court': {
    main: '#0097a7', // Teal
    light: '#e0f7fa',
  },
  'DECISION - Administrative Decision': {
    main: '#00796b', // Green-Teal
    light: '#e0f2f1',
  },
  'DECISION - Tax Committee Appeal': {
    main: '#5e35b1', // Deep Purple
    light: '#ede7f6',
  },
  // Alternate color options
  alt1: {
    main: '#f57c00', // Deep Orange
    light: '#fff8e1',
  },
  alt2: {
    main: '#00838f', // Cyan
    light: '#e0f7fa',
  },
  alt3: {
    main: '#558b2f', // Light Green
    light: '#f1f8e9',
  },
  alt4: {
    main: '#6a1b9a', // Dark Purple
    light: '#f3e5f5',
  },
  alt5: {
    main: '#ad1457', // Dark Pink
    light: '#fce4ec',
  },
  alt6: {
    main: '#1565c0', // Dark Blue
    light: '#e3f2fd',
  },
  alt7: {
    main: '#c62828', // Red
    light: '#ffebee',
  },
  alt8: {
    main: '#424242', // Dark Grey
    light: '#fafafa',
  },
  alt9: {
    main: '#ff5722', // Deep Orange
    light: '#fbe9e7',
  },
  alt10: {
    main: '#37474f', // Blue Grey
    light: '#eceff1',
  },
  default: {
    main: '#673ab7', // Default Purple
    light: '#ede7f6',
  },
}

// Helper function to get color based on decision type
const getDecisionTypeColor = (type) => {
  return decisionTypeColors[type] || decisionTypeColors.default
}

// Helper function to get abbreviated type label
const getTypeLabel = (type) => {
  if (!type) return 'Decision'

  if (type.includes('MD - Ministerial Decision')) return 'Ministerial Decision'
  if (type.includes('FTA - Federal Tax Authority Decision'))
    return 'Federal Tax Authority Decision'
  if (type.includes('AD - Administrative Decision'))
    return 'Administrative Decision'
  if (type.includes('CD - Cabinet Decision')) return 'Cabinet Decision'
  if (type.includes('ZD - ZATCA Decision')) return 'ZATCA Decision'
  if (type.includes('BL - Bylaws')) return 'Bylaws'
  if (type.includes('ER - Executive Rules and Instructions')) return 'Executive Rules and Instructions'
  if (type.includes('ERS - Executive Regulations')) return 'Executive Regulations'
  if (type.includes('IR - Implementing Regulations')) return 'Implementing Regulations'
  if (type.includes('EB - Executive Bylaws')) return 'Executive Bylaws'
  if (type.includes('JD - Judicial Decision')) return 'Judicial Decision'

  // Default fallback
  return 'Decision'
}

// Display name as heading and title as subHeading
const DecisionCard = ({ article, searchQuery, showSnackbar }) => {
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth()

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('') // Feedback submitted successfully 😄
  const [snackbarSeverity, setSnackbarSeverity] = useState('success') // success or error

  const modalContentRef = useRef(null)

  // Scope browser Ctrl+F to this modal's content while it is open.
  useFindScopeLayer(modalIsOpen, 'card-modal')

  // Theme colors
  const themeColors = {
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
  }

  // Get colors based on decision type
  const typeColors = getDecisionTypeColor(article?.type)

  // Consistent button border radius
  const buttonBorderRadius = '28px'

  const handleModalToggle = useCallback(() => {
    // Check if user is authenticated before opening modal
    if (!isAuthenticated) {
      handleOpenInNewTab(article, `/decisions/${article?.slug}`, showSnackbar)
      return
    }
    setModalIsOpen((prev) => !prev)
    // handleOpenInNewTab(article, `/decisions/${article?.slug}`, showSnackbar)
  }, [article, isAuthenticated, showSnackbar])
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return
    setSnackbarOpen(false)
  }
  const handleShowSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message || '')
    setSnackbarSeverity(severity || 'info')
    setSnackbarOpen(true)
  }, [])
  // Handle ESC key press - close modal only if no popups are open
  // Popup closing order: NestedPopup (top) -> MainPopup -> Modal (bottom)
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && modalIsOpen) {
        // Check if NestedPopup is open (highest priority - closes first)
        const nestedPopup = document.getElementById('nested-popup-container')
        if (nestedPopup) {
          // NestedPopup is open, let it handle its own ESC key
          return
        }
        
        // Check if MainPopup is open (second priority)
        const mainPopup = document.getElementById('main-popup-container')
        if (mainPopup) {
          // MainPopup is open, let it handle its own ESC key
          return
        }
        
        // No popups are open, close the Modal
        handleModalToggle()
      }
    }

    if (modalIsOpen) {
      document.addEventListener('keydown', handleEscKey)
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [modalIsOpen, handleModalToggle])

  // Set up scroll detection with direct DOM approach
  useEffect(() => {
    // Only run this if modal is open
    if (!modalIsOpen) return

    // Use requestAnimationFrame for smoother detection
    let scrollFrame
    const checkScroll = () => {
      if (modalContentRef.current) {
        // Check if scrolled down enough
        setShowBackToTop(modalContentRef.current.scrollTop > 300)
      }
      // Continue checking
      scrollFrame = requestAnimationFrame(checkScroll)
    }

    // Start the animation frame loop
    scrollFrame = requestAnimationFrame(checkScroll)

    // Add a basic scroll listener as backup
    const handleScroll = () => {
      if (modalContentRef.current) {
        setShowBackToTop(modalContentRef.current.scrollTop > 300)
      }
    }

    // Get the modal element
    const modalElement = modalContentRef.current
    if (modalElement) {
      modalElement.addEventListener('scroll', handleScroll)
    }

    // Clean up
    return () => {
      cancelAnimationFrame(scrollFrame)
      if (modalElement) {
        modalElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [modalIsOpen])

  // Function to scroll modal to top
  const scrollToTop = useCallback(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [])

  // Truncate long text
  // TODO1: updated version of truncateText is present only in ArticleCard and not in DecisionCard, GuideCard, TaxTreatyCard
  // issue: any html code should be ignored while displaying text of title/name/country2Name,  for e.g. "Article 26 - <a href='/decisions/uae-cit-fdl-47-2022-md-132-of-2023' target='_blank' rel='noopener noreferrer'>Transfers Within a Qualifying Group</a>" so this should be considered as ""Article 26 - Transfers Within a Qualifying Group" before using slicing,  but we dont want to remove the effect html tags were giving like bold italic text color change etc
  // TODO2: also tooltip should not show html code while hovering over title
  // const truncateText = (text, maxLength) => {
  //   if (!text) return ''
  //   if (text.length <= maxLength) return text
  //   return text.slice(0, maxLength) + '...'
  // }
  const truncateText = (html, maxLength) => {
    if (!html) return ''

    const div = document.createElement('div')
    div.innerHTML = html

    let visibleCount = 0
    let truncated = ''

    const walk = (node) => {
      if (visibleCount >= maxLength) return

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent
        const remaining = maxLength - visibleCount

        if (text.length <= remaining) {
          truncated += text
          visibleCount += text.length
        } else {
          truncated += text.slice(0, remaining) + '...'
          visibleCount = maxLength
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        truncated += `<${node.nodeName.toLowerCase()}${[...node.attributes]
          .map((attr) => ` ${attr.name}="${attr.value}"`)
          .join('')}>`
        for (let child of node.childNodes) walk(child)
        truncated += `</${node.nodeName.toLowerCase()}>`
      }
    }

    for (let child of div.childNodes) {
      if (visibleCount < maxLength) walk(child)
    }

    return truncated
  }
  // Calculate card height based on screen size
  const cardHeight = isMobileScreen
    ? '200px' // smaller screen
    : isMediumScreen
    ? '170px' // medium screen
    : '170px' // large screen

  const getPlainText = (html) => {
    if (!html) return ''
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }

  // Create truncated versions of name and title with full versions for tooltip
  const truncatedName = truncateText(article?.name, isMobileScreen ? 60 : 80)
  const plainTextName = getPlainText(article?.name)
  // const plainTextNameTruncated = truncateText(
  //   plainTextName,
  //   isMobileScreen ? 60 : 80
  // )
  const truncatedTitle = truncateText(article?.title, isMobileScreen ? 90 : 120)
  const plainTextTitle = getPlainText(article?.title)
  // const plainTextTitleTruncated = truncateText(
  //   plainTextTitle,
  //   isMobileScreen ? 90 : 120
  // )
  const showNameTooltip = plainTextName.length > (isMobileScreen ? 60 : 80)
  const showTitleTooltip = plainTextTitle.length > (isMobileScreen ? 90 : 120)

  // Format the year for display
  const displayYear = article?.year ? `${article.year}` : ''

  // Get decision number if available
  const decisionNumber = article?.decisionNumber || ''

  return (
    <>
      <Paper
        onClick={handleModalToggle}
        sx={{
          margin: 2,
          padding: 2,
          cursor: 'pointer',
          height: cardHeight,
          // minHeight: '150px'
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          },
          borderLeft: `5px solid ${typeColors.main}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            background: `linear-gradient(90deg, ${typeColors.light} 0%, rgba(255,255,255,0) 50%)`,
            opacity: 0.5,
            zIndex: 0,
            pointerEvents: 'none',
          },
        }}
        id={`article-${article?.slug}`}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            zIndex: 1,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flexGrow: 1,
              overflow: 'hidden',
              mr: isMobileScreen ? 1 : 2,
            }}
          >
            {/* Main heading - Name */}
            <Tooltip
              title={showNameTooltip ? plainTextName : ''}
              arrow
              placement='top'
            >
              <Typography
                variant={isMobileScreen ? 'subtitle1' : 'h6'}
                sx={{
                  cursor: 'pointer',
                  color: themeColors.primary,
                  lineHeight: isMobileScreen ? 1.3 : 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mb: 1,
                  fontWeight: 600,
                  fontSize: isMobileScreen ? '1rem' : undefined,
                  '& a': {
                    pointerEvents: 'none',
                    color: 'inherit',
                    textDecoration: 'inherit',
                  },
                }}
              >
                {parseHtmlSanitizeAddTargetToLinks(truncatedName, searchQuery)}
              </Typography>
            </Tooltip>

            {/* Subheading - Title */}
            <Tooltip
              title={showTitleTooltip ? plainTextTitle : ''}
              arrow
              placement='bottom'
            >
              <Typography
                variant='body2'
                sx={{
                  cursor: 'pointer',
                  color: 'text.secondary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.3,
                  fontSize: isMobileScreen ? '0.75rem' : '0.875rem',
                  mb: 1,
                  '& a': {
                    pointerEvents: 'none',
                    color: 'inherit',
                    textDecoration: 'inherit',
                  },
                }}
              >
                {parseHtmlSanitizeAddTargetToLinks(truncatedTitle, searchQuery)}
              </Typography>
            </Tooltip>
            {/* Type and metadata chips */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobileScreen ? 'column' : 'row',
                alignItems: isMobileScreen ? 'flex-start' : 'center',
                gap: 1,
                mt: 'auto',
                flexWrap: 'wrap',
              }}
            >
              <Chip
                label={getTypeLabel(article?.type)}
                size={isMobileScreen ? 'small' : 'medium'}
                sx={{
                  backgroundColor: typeColors.main,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: isMobileScreen ? '0.7rem' : '0.75rem',
                  height: isMobileScreen ? '24px' : '28px',
                }}
              />

              {displayYear && (
                <Chip
                  label={displayYear}
                  size={isMobileScreen ? 'small' : 'medium'}
                  variant='outlined'
                  sx={{
                    borderColor: typeColors.main,
                    color: typeColors.main,
                    fontSize: isMobileScreen ? '0.7rem' : '0.75rem',
                    height: isMobileScreen ? '24px' : '28px',
                  }}
                />
              )}

              {decisionNumber && (
                <Chip
                  label={`No. ${decisionNumber}`}
                  size={isMobileScreen ? 'small' : 'medium'}
                  variant='outlined'
                  sx={{
                    borderColor: typeColors.main,
                    color: typeColors.main,
                    fontSize: isMobileScreen ? '0.7rem' : '0.75rem',
                    height: isMobileScreen ? '24px' : '28px',
                  }}
                />
              )}
              {article?.betaVersion && (
                <Chip
                  label='Beta Version'
                  size={isMobileScreen ? 'small' : 'medium'}
                  sx={{
                    bgcolor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 600,
                    fontSize: isMobileScreen ? '0.7rem' : '0.75rem',
                    height: isMobileScreen ? '24px' : '28px',
                    border: '1px solid rgba(25, 118, 210, 0.25)',
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Right side button */}
          <Box
            sx={{
              alignSelf: 'flex-start',
              // ,flexShrink: 0, ml: 1
            }}
          >
            <Tooltip title='Open in new tab' arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenInNewTab(article, `/decisions/${article?.slug}`)
                }}
                size={isMobileScreen ? 'small' : 'medium'}
                sx={{
                  color: themeColors.primary,
                  // p: isMobileScreen ? 0.5 : 1,
                  '&:hover': {
                    backgroundColor: 'rgba(35, 37, 54, 0.08)',
                  },
                }}
              >
                <OpenInNewRoundedIcon
                  fontSize={isMobileScreen ? 'small' : 'medium'}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* "Read more" button/link at the bottom of card */}
        {/* <Box
        sx={{
          alignSelf: 'flex-start',
          mt: 'auto',
          pt: 1,
        }}
      >
        <Button
          onClick={handleModalToggle}
          size={isMobileScreen ? 'small' : 'medium'}
          sx={{
            textTransform: 'none',
            color: themeColors.primary,
            fontWeight: 500,
            p: isMobileScreen ? '2px 8px' : '4px 10px',
            minWidth: 0,
            '&:hover': {
              backgroundColor: 'rgba(35, 37, 54, 0.08)',
            },
          }}
        >
          Read more
        </Button>
      </Box> */}

        {/* Modal for full content */}
        <Modal open={modalIsOpen} onClose={handleModalToggle} disableEscapeKeyDown disableEnforceFocus>
          {/* Wrapper Box to maintain border radius when scrollbar appears */}
          <Box
            data-find-scope='card-modal'
            onClick={(e) => e.stopPropagation()}
            sx={{
              margin: '5vh auto',
              width: isMobileScreen ? '95vw' : isMediumScreen ? '90vw' : '85vw',
              maxHeight: '90vh',
              borderRadius: '8px',
              overflow: 'hidden', // Hide any content that would break the border radius
              position: 'relative',
              display: 'flex', // Use flexbox
              flexDirection: 'column', // Stack content vertically
              backgroundColor: 'white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              borderLeft: `5px solid ${typeColors.main}`,
            }}
          >
            {/* Content Box with scrolling */}
            <Box
              ref={modalContentRef}
              sx={{
                padding: '1px 6px 16px 6px', // Add padding inside the content area
                paddingTop: 2,
                position: 'relative',
                overflowY: 'auto', // Enable vertical scrolling
                flexGrow: 1, // Allow this box to expand
                // Ensure scrollbar doesn't affect layout
                scrollbarWidth: 'thin', // For Firefox
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '0 8px 8px 0', // Round the scrollbar track
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              {/* RESPONSIVE CLOSE BUTTON */}
              {isMobileScreen ? (
                // Smaller close button for mobile
                <IconButton
                  onClick={handleModalToggle}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    float: 'right',
                    zIndex: 1500,
                    backgroundColor: 'white',
                    border: '1px solid #232536',
                    margin: '-8px -8px 0 0',
                    right: '-2px', // Use right positioning for exact alignment
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  }}
                  aria-label='close modal'
                  size='small'
                >
                  <CloseIcon sx={{ color: '#232536' }} fontSize='small' />
                </IconButton>
              ) : (
                // Larger close button with text for desktop/tablet
                <Box
                  sx={{
                    position: 'sticky',
                    cursor: 'pointer',
                    top: 0,
                    float: 'right',
                    zIndex: 1500,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    border: '1px solid #232536',
                    borderRadius: buttonBorderRadius,
                    margin: '-4px -4px 0 0',
                    right: 0,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    padding: '4px 12px',
                  }}
                  onClick={handleModalToggle}
                  role='button'
                  aria-label='close modal'
                >
                  <CloseIcon
                    sx={{ color: themeColors.primary }}
                    fontSize='small'
                  />
                  <Box sx={{ alignSelf: 'center' }}>
                    <Typography
                      variant='button'
                      sx={{
                        color: themeColors.primary,
                        marginLeft: 0.5,
                        fontSize: '0.80rem',
                        fontWeight: 'bold',
                      }}
                    >
                      Close
                    </Typography>
                  </Box>
                </Box>
              )}
              {/* Decision metadata chips in modal */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mt: isMobileScreen ? 4 : 2,
                  mb: 2,
                  ml: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Chip
                  label={article?.type || 'Decision'}
                  size='medium'
                  sx={{
                    backgroundColor: typeColors.main,
                    color: 'white',
                    fontWeight: 600,
                    px: 1,
                  }}
                />

                {displayYear && (
                  <Chip
                    label={displayYear}
                    variant='outlined'
                    sx={{
                      borderColor: typeColors.main,
                      color: typeColors.main,
                    }}
                  />
                )}

                {decisionNumber && (
                  <Chip
                    label={`Decision No. ${decisionNumber}`}
                    variant='outlined'
                    sx={{
                      borderColor: typeColors.main,
                      color: typeColors.main,
                    }}
                  />
                )}
                {article?.betaVersion && (
                  <Chip
                    label='Beta Version'
                    size={isMobileScreen ? 'small' : 'medium'}
                    sx={{
                      bgcolor: '#e3f2fd',
                      color: '#1976d2',
                      fontWeight: 600,
                      fontSize: isMobileScreen ? '0.7rem' : '0.75rem',
                      height: isMobileScreen ? '24px' : '28px',
                      border: '1px solid rgba(25, 118, 210, 0.25)',
                    }}
                  />
                )}
              </Box>

              {/* Conditionally render DecisionArticle component only if authenticated */}
              {isLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    padding: 4,
                  }}
                >
                  <CircularProgress sx={{ color: themeColors.primary }} />
                  <Typography
                    variant='body2'
                    sx={{
                      mt: 2,
                      color: 'text.secondary',
                    }}
                  >
                    Checking authentication...
                  </Typography>
                </Box>
              ) : isAuthenticated ? (
                <DecisionArticle
                  article={article}
                  searchQuery={searchQuery}
                  showSnackbar={handleShowSnackbar}
                  isInModal={true}
                  parentContainerRef={modalContentRef}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    padding: 4,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: themeColors.primary,
                    }}
                  >
                    Authentication Required
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      mb: 3,
                      color: 'text.secondary',
                      maxWidth: '500px',
                    }}
                  >
                    Please log in to view this decision. You need to be authenticated
                    to access decision content.
                  </Typography>
                  <Button
                    variant='contained'
                    onClick={() => {
                      // Store current path for redirect after login
                      sessionStorage.setItem(
                        'redirectPath',
                        `/decisions/${article?.slug}`
                      )
                      navigate('/login')
                    }}
                    sx={{
                      backgroundColor: themeColors.primary,
                      color: 'white',
                      padding: '10px 24px',
                      borderRadius: buttonBorderRadius,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: themeColors.hover,
                      },
                    }}
                  >
                    Go to Login
                  </Button>
                </Box>
              )}

              <Box
                sx={{
                  position: 'sticky',
                  bottom: isMobileScreen ? 20 : 25,
                  // right: 12, // Adjusted to account for scrollbar
                  float: 'right',
                  marginTop: '-60px',
                  zIndex: 1400,
                }}
              >
                <Zoom in={showBackToTop}>
                  {isMobileScreen ? (
                    // Small circular Fab for mobile
                    <Fab
                      size='small'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: '#232536',
                        color: 'white',
                        '&:hover': { backgroundColor: '#3a3e5a' },
                      }}
                    >
                      <KeyboardArrowUpIcon fontSize='small' />
                    </Fab>
                  ) : (
                    // Extended Fab with text for desktop/tablet
                    <Fab
                      variant='extended'
                      size='medium'
                      aria-label='Scroll back to top'
                      onClick={scrollToTop}
                      sx={{
                        backgroundColor: themeColors.primary,
                        color: 'white',
                        '&:hover': { backgroundColor: themeColors.hover },
                        paddingLeft: 2,
                        paddingRight: 2,
                        borderRadius: buttonBorderRadius,
                      }}
                    >
                      <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                      <Typography
                        variant='button'
                        sx={{
                          // color: themeColors.primary,
                          // marginLeft: 0.5,
                          // fontSize: '0.80rem',
                          fontWeight: 'bold',
                        }}
                      >
                        Back to Top
                      </Typography>
                    </Fab>
                  )}
                </Zoom>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            // backgroundColor:
            //   snackbarSeverity === 'warning'
            //     ? '#ffcf51'
            //     : snackbarSeverity === 'error'
            //     ? '#d32f2f'
            //     : snackbarSeverity === 'success'
            //     ? '#43a047'
            //     : '#232536',
            // color: snackbarSeverity === 'warning' ? '#232536' : '#fff',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
export default DecisionCard
